import React from "react";
import {
  Table,
  Segment,
  Button,
  Dropdown,
  Message,
  Label,
  Header,
} from "semantic-ui-react";
import { Form, Formik, getIn } from "formik";
import * as Yup from "yup";

const GroupStep = ({
  supplyUnits,
  setSupplyUnits,
  onStepComplete,
  onPrevious,
}) => {
  const unitsToGroup = supplyUnits.filter((unit) => unit.assign_new_inventory);

  const getGroupOptions = (quantity) => {
    const idValues = Array.from({ length: quantity }, (x, i) => -(i + 1));

    const options = idValues.map((groupId) => ({
      key: groupId,
      value: groupId,
      text: `Group ${Math.abs(groupId)}`,
    }));

    return options;
  };

  // If there's only one group option, use it as default
  const defaultGroupId = unitsToGroup.length === 1 ? -1 : "";

  const initialValues = {
    units: unitsToGroup.reduce((acc, unit, index) => {
      const originalIndex = supplyUnits.findIndex((u) => u.upc === unit.upc);
      acc[`upc_${unit.upc}`] = {
        originalIndex,
        groupId: unit.new_inventory_id || defaultGroupId,
      };
      return acc;
    }, {}),
  };

  const validationSchema = Yup.object({
    units: Yup.object()
      .shape(
        unitsToGroup.reduce((schema, unit) => {
          schema[`upc_${unit.upc}`] = Yup.object({
            originalIndex: Yup.number().required(),
            groupId: Yup.mixed().required("Group assignment is required"),
          });
          return schema;
        }, {})
      )
      .default(undefined)
      .required(),
  });

  const handleSubmit = (values, { setSubmitting }) => {
    const updatedSupplyUnits = [...supplyUnits];
    const units = values.units;

    Object.keys(units).forEach((prefixedUpc) => {
      const { originalIndex, groupId } = units[prefixedUpc];
      updatedSupplyUnits[originalIndex] = {
        ...updatedSupplyUnits[originalIndex],
        new_inventory_id: groupId,
      };
    });

    setSupplyUnits(updatedSupplyUnits);

    const formattedValues = {
      units: Object.entries(units).reduce((acc, [key, value]) => {
        const upc = key.replace("upc_", "");
        acc[upc] = value;
        return acc;
      }, {}),
    };

    onStepComplete(formattedValues);
    setSubmitting(false);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      {({ isSubmitting, errors, touched, values, setFieldValue }) => (
        <Form>
          <Segment>
            <>
              <Header as="h4">Group Inventory Items</Header>
              <p>
                Assign each item to a group. Items in the same group will use
                the same new inventory ID.
              </p>

              {Object.keys(errors).length > 0 && touched.units && (
                <Message negative>
                  <Message.Header>
                    Please complete all group assignments
                  </Message.Header>
                  <p>
                    Each item must be assigned to a group before proceeding.
                  </p>
                </Message>
              )}

              <Table celled>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>UPC</Table.HeaderCell>
                    <Table.HeaderCell>Title</Table.HeaderCell>
                    <Table.HeaderCell>Group Assignment</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {unitsToGroup.map((unit, index) => {
                    const fieldName = `units.upc_${unit.upc}.groupId`;
                    const fieldTouched = getIn(touched, fieldName);
                    const error = getIn(errors, fieldName);

                    return (
                      <Table.Row key={`${unit.upc}-${index}`}>
                        <Table.Cell>{unit.upc}</Table.Cell>
                        <Table.Cell>{unit.supply_title}</Table.Cell>
                        <Table.Cell>
                          <div>
                            <Dropdown
                              placeholder="Select Group"
                              fluid
                              selection
                              options={getGroupOptions(unitsToGroup.length)}
                              value={
                                values.units[`upc_${unit.upc}`]?.groupId || ""
                              }
                              onChange={(e, { value }) => {
                                setFieldValue(fieldName, value);
                              }}
                              error={fieldTouched && error ? true : false}
                            />
                            {fieldTouched && error ? (
                              <Label pointing prompt>
                                {error}
                              </Label>
                            ) : null}
                            {values.units[`upc_${unit.upc}`]?.groupId &&
                              values.units[`upc_${unit.upc}`]?.groupId !==
                                "new" && (
                                <Label
                                  color="blue"
                                  style={{ marginTop: "5px" }}
                                >
                                  Group{" "}
                                  {Math.abs(
                                    values.units[`upc_${unit.upc}`].groupId
                                  )}
                                </Label>
                              )}
                          </div>
                        </Table.Cell>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>

              <div
                style={{
                  marginTop: "20px",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Button type="button" onClick={onPrevious}>
                  Back
                </Button>
                <Button type="submit" primary disabled={isSubmitting}>
                  Next
                </Button>
              </div>
            </>
          </Segment>
        </Form>
      )}
    </Formik>
  );
};

export default GroupStep;
